import CenteredLoader from '@client/components/layout/CenteredLoader';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScaledPosition } from 'react-pdf-highlighter';
import { AreaHighlight, Highlight, PdfHighlighter, PdfLoader } from 'react-pdf-highlighter';
import 'react-pdf-highlighter/dist/style.css';
import Styles from './Styles.module.scss';
import ViewerHeader from './ViewerHeader';

const PdfHighligter = ({ fileUrl, highlightedText }: { fileUrl: string; highlightedText: any }) => {
    const pdfHighlighterRef = useRef<any>(null);
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    const [pdfDocument, setPdfDocument] = useState(null);
    const [isPdfRendered, setIsPdfRendered] = useState(false);
    const previousFileUrlRef = useRef<string | null>(null);

    useEffect(() => {
        if (previousFileUrlRef.current !== fileUrl) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setPdfDocument(null);
            setIsPdfLoaded(false);
            setIsPdfRendered(false);
            previousFileUrlRef.current = fileUrl;
        }
    }, [fileUrl]);

    const handlePdfDocument = useCallback(
        (newPdfDocument: any) => {
            if (newPdfDocument && newPdfDocument !== pdfDocument) {
                setPdfDocument(newPdfDocument);
                setIsPdfLoaded(true);

                setTimeout(() => {
                    setIsPdfRendered(true);
                }, 1000);
            }
        },
        [pdfDocument]
    );

    useEffect(() => {
        if (!highlightedText) return;

        if (!isPdfLoaded || !isPdfRendered) return;

        const container = pdfHighlighterRef.current?.containerNode;
        if (!container) return;

        const scrollToHighlight = () => {
            const id = highlightedText?.content?.text ? 'highlightedText' : 'highlightedArea';
            const highlightEl = container?.querySelector(`[data-id="${id}"]`);

            if (highlightEl && container) {
                const containerRect = container.getBoundingClientRect();
                const highlightRect = highlightEl.getBoundingClientRect();
                const targetScroll =
                    container.scrollTop +
                    (highlightRect.top + highlightRect.height / 2 - containerRect.top - containerRect.height / 2);

                container.scrollTo({
                    top: targetScroll,
                    behavior: 'smooth',
                });
            }
        };

        const timer = setTimeout(scrollToHighlight, 500);
        return () => clearTimeout(timer);
    }, [highlightedText, isPdfLoaded, isPdfRendered]);

    return (
        <div className={Styles.mainContainer}>
            <ViewerHeader />
            <div className={Styles.pdfHighlighterContainer}>
                {
                    <PdfLoader
                        url={fileUrl}
                        beforeLoad={() => <CenteredLoader loading={true} />}
                    >
                        {(pdfDocumentFromLoader: any) => {
                            handlePdfDocument(pdfDocumentFromLoader);
                            if (!isPdfLoaded || !pdfDocument) return <CenteredLoader loading={true} />;
                            return (
                                <PdfHighlighter
                                    ref={pdfHighlighterRef}
                                    pdfDocument={pdfDocument}
                                    scrollRef={() => {}}
                                    onScrollChange={() => {}}
                                    enableAreaSelection={event => event.altKey}
                                    onSelectionFinished={(position: ScaledPosition, content, hideTipAndSelection) => {}}
                                    highlightTransform={(
                                        highlight,
                                        index,
                                        setTip,
                                        hideTip,
                                        viewportToScaled,
                                        screenshot,
                                        isScrolledTo
                                    ) => {
                                        // Text highlight
                                        if (highlight?.content?.text) {
                                            return (
                                                <div
                                                    data-id="highlightedText"
                                                    className={Styles.lineHighlight}
                                                >
                                                    <Highlight
                                                        isScrolledTo={isScrolledTo}
                                                        position={highlight.position}
                                                        comment={highlight.comment}
                                                    />
                                                </div>
                                            );
                                        }

                                        // Area highlight
                                        return (
                                            <div
                                                data-id="highlightedArea"
                                                className={Styles.areaHighlight}
                                            >
                                                <AreaHighlight
                                                    isScrolledTo={isScrolledTo}
                                                    highlight={highlight}
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        );
                                    }}
                                    highlights={highlightedText && isPdfRendered ? [highlightedText] : []}
                                />
                            );
                        }}
                    </PdfLoader>
                }
            </div>
        </div>
    );
};

export default PdfHighligter;
